import {
  selectOrganizationId,
  useCreateDemoMutation,
  useDuplicateDemoMutation,
  useInfiniteScroll,
  useLazyGetSearchResultsQuery,
} from '@carboncopyco/data';
import {
  DemoIndicatorColoredIcon,
  PlusOutlineIcon,
  RecordingIndicatorColoredIcon,
} from '@carboncopyco/icons';
import {
  ActionDemoIllustration,
  SearchNoMatchIllustration,
} from '@carboncopyco/illustrations';
import {
  type CreatorFilter,
  type Entity,
  type IDemo,
  type IRecording,
  RecordingTypes,
  type Sort,
} from '@carboncopyco/types';
import {
  Box,
  Button,
  ColorHex,
  EmptyView,
  ErrorBox,
  Flex,
  Grid,
  InputText,
  LoadingDots,
  Modal,
  ScrollContainer,
  SkeletonImageLoading,
  Spacer,
  TextBase,
  TextLargeParagraph,
  TileAction,
  TooltipBubble,
} from '@carboncopyco/ui';
import {
  AnalyticsTrackEventName,
  MMMDoYYYY,
  useAnalytics,
} from '@carboncopyco/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMatch, useParams } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import { useWorkspacesMap } from '../../../hooks/useWorkspacesMap';
import { TileImageSquare } from '../../GridTiles';
import SearchControls from '../../SearchControls';
import { useWorkspacePageProps } from '../../Workspaces/WorkspacePage/WorkspacePageContext';
import {
  creatorFilterOptions,
  sortOptions,
  typeFilterOptions,
} from '../../consts';

type CreateDemoModalProps = {
  open: boolean;
  onClose: () => void;
  noBackground?: boolean;
  handleOpenRecorder: (workspaceId?: string, folderId?: string) => void;
  goToDemo: (demoId: string, workspaceId?: string | undefined) => void;
};

export const CreateDemoModal = ({
  open,
  onClose,
  noBackground,
  handleOpenRecorder,
  goToDemo,
}: CreateDemoModalProps) => {
  const organizationId = useSelector(selectOrganizationId);
  const {
    items,
    error,
    isLoading,
    isLoadingBatch,
    fetchFirstBatch,
    fetchNextBatch,
  } = useInfiniteScroll(useLazyGetSearchResultsQuery);
  const flags = useFlags();
  const workspacesMap = useWorkspacesMap();
  const { workspaceId } = useParams<{
    workspaceId: string;
  }>();
  const { folderId } = useWorkspacePageProps();
  const [createDemo, { isLoading: isLoadingCreateDemo }] =
    useCreateDemoMutation();
  const [duplicateDemo, { isLoading: isLoadingDuplicateDemo }] =
    useDuplicateDemoMutation();
  const isHomepage = useMatch('/:organizationId');
  const [creatorFilter, setCreatorFilter] = useState<CreatorFilter>('all');
  const [typeFilter, setTypeFilter] = useState<Entity | 'all'>('all');
  const [sort, setSort] = useState<Sort>('alphabet');
  const [inputValue, setInputValue] = useState<string>('');
  const [query] = useDebounce(inputValue, 500);
  const resetFilters = () => {
    setCreatorFilter('all');
    setTypeFilter('all');
  };
  const isAllControlsDefault =
    typeFilter === 'all' && sort === 'alphabet' && creatorFilter === 'all';

  useEffect(() => {
    fetchFirstBatch({
      organizationId,
      workspaces: workspaceId ?? 'all',
      query,
      mineOnly: creatorFilter === 'mineOnly',
      entities: typeFilter === 'all' ? ['demo', 'record'] : [typeFilter],
      sort,
      recordingType: [RecordingTypes.dynamic, RecordingTypes.native],
      includeShared: false,
    });
  }, [
    typeFilter,
    creatorFilter,
    sort,
    query,
    workspaceId,
    fetchFirstBatch,
    organizationId,
  ]);

  const { trackEvent } = useAnalytics();
  const isLoadingNew = isLoadingDuplicateDemo || isLoadingCreateDemo;

  const navigateToDemo = (demo: IDemo) => {
    onClose();
    if (isHomepage && flags.platformWorkspaces) {
      goToDemo(String(demo.id), demo.workspaceId);
      return;
    }
    goToDemo(String(demo.id));
  };

  const handleNewClone = () => {
    if (isHomepage && flags.platformWorkspaces) {
      handleOpenRecorder(String(workspaceId));
      onClose();
      return;
    }
    onClose();
    handleOpenRecorder(workspaceId, folderId);
  };
  const handleCreateDemo = (selectedItem: any) => {
    if ('demoName' in selectedItem) {
      duplicateDemo({
        organizationId,
        id: selectedItem.id,
        folderId,
      })
        .unwrap()
        .then((response) => {
          trackEvent(AnalyticsTrackEventName.DEMO_CLICK_DUPLICATE, {
            location: 'create_demo_modal',
            cloneId: selectedItem.id,
            demoName: selectedItem,
          });
          navigateToDemo(response);
        });
    } else {
      createDemo({
        organizationId,
        recordingId: Number(selectedItem.id),
        demoName: `Demo for ${(selectedItem as IRecording).recordName}`,
        folderId,
      })
        .unwrap()
        .then((response) => {
          trackEvent('create_new_demo', {
            location: 'create_demo_modal',
            recordingId: selectedItem.id,
            demoName: selectedItem,
          });
          navigateToDemo(response);
        });
    }
  };
  return (
    <Modal
      noBackground={noBackground}
      onClose={onClose}
      open={open}
      title="Choose a base for your new demo"
    >
      <div className="w-ds-lg-modal h-ds-440 flex flex-col">
        {error ? (
          <div className="h-full w-full">
            <ErrorBox
              contained
              title="Error fetching demos"
            />
          </div>
        ) : isLoadingNew ? (
          <div className="h-full w-full flex flex-col items-center justify-center">
            <div className="my-ds-10">
              <LoadingDots />
            </div>
            <TextBase
              color="text-ds-natural-600"
              label="Please wait while creating new demo"
            />
          </div>
        ) : (
          <>
            <Spacer amount={1} />
            <TextBase label="Use an existing clone or demo, or create a new one." />
            <Spacer amount={0.75} />
            <div className="flex justify-between">
              <SearchControls
                creatorFilterDropdown={{
                  value: creatorFilter,
                  onSelect: setCreatorFilter,
                  items: creatorFilterOptions,
                }}
                typeFilterDropdown={{
                  value: typeFilter,
                  onSelect: setTypeFilter,
                  items: typeFilterOptions.filter(
                    (type) =>
                      type.id === 'all' ||
                      type.id === 'record' ||
                      type.id === 'demo'
                  ),
                }}
                sortDropdown={{
                  value: sort,
                  onSelect: setSort,
                  items: sortOptions,
                }}
              />
              <div className="w-96">
                <InputText
                  value={inputValue}
                  placeholder="Search"
                  onChange={(event) => {
                    setInputValue(event.currentTarget.value);
                  }}
                />
              </div>
            </div>
            <Spacer amount={1.75} />
            <Box className="flex-grow">
              {isLoading ? (
                <SkeletonImageLoading cols={4} />
              ) : items?.length || (!query && isAllControlsDefault) ? (
                <ScrollContainer
                  border
                  onReachBottom={() => {
                    fetchNextBatch({
                      organizationId,
                      workspaces: workspaceId ?? 'all',
                      query,
                      mineOnly: creatorFilter === 'mineOnly',
                      entities:
                        typeFilter === 'all'
                          ? ['demo', 'record']
                          : [typeFilter],
                      sort,
                      includeShared: false,
                    });
                  }}
                >
                  <Grid
                    data={items}
                    actionItem={
                      typeFilter !== 'demo' && (
                        <div className="p-2.5">
                          <TileAction
                            onClick={() => {
                              trackEvent(
                                AnalyticsTrackEventName.DEMO_CLICK_OPEN_CLONER,
                                {
                                  location: 'create_demo_modal',
                                }
                              );
                              handleNewClone();
                            }}
                            title="New clone"
                            icon={<PlusOutlineIcon />}
                            illustration={<ActionDemoIllustration />}
                          />
                        </div>
                      )
                    }
                    onItemClick={(item) => {
                      handleCreateDemo(item);
                    }}
                    renderItem={(itemData, selected, onItemClick) => {
                      const isDemo = 'demoName' in itemData;
                      const isPlaybook = 'assets' in itemData;
                      const title = isDemo
                        ? (itemData as IDemo).demoName || 'Untitled demo'
                        : (itemData as IRecording).recordName ||
                          'Untitled clone';
                      const workspace = workspacesMap[itemData.workspaceId] ?? {
                        workspaceName: 'Untitled workspace',
                      };
                      return (
                        <TooltipBubble
                          title={`${title}, ${workspace.workspaceName}`}
                          placement="top"
                          interactive={false}
                          followCursor
                          disabled={!flags.platformWorkspaces}
                        >
                          <TileImageSquare
                            selected={itemData.id === selected}
                            smallImage={!isDemo}
                            onClick={() => onItemClick?.(itemData)}
                            title={title}
                            caption={
                              flags.platformWorkspaces
                                ? workspace.workspaceName
                                : ''
                            }
                            createTime={MMMDoYYYY(itemData.createTime)}
                            image={isPlaybook ? '' : itemData?.image}
                            icon={
                              isDemo ? (
                                <DemoIndicatorColoredIcon
                                  color={ColorHex.accent3100}
                                  size={30}
                                />
                              ) : (
                                <RecordingIndicatorColoredIcon
                                  color={ColorHex.accent3100}
                                  size={30}
                                />
                              )
                            }
                          />
                        </TooltipBubble>
                      );
                    }}
                  />
                  <div className="flex justify-center min-ds-5 pt-ds-5">
                    {isLoadingBatch && <LoadingDots />}
                  </div>
                </ScrollContainer>
              ) : (
                <EmptyView
                  illustration={<SearchNoMatchIllustration />}
                  title={
                    <Flex
                      col
                      alignItems="center"
                    >
                      <div className="mb-ds-4">
                        <TextLargeParagraph
                          color="text-ds-natural-600"
                          label={`No matches found for "${query}"`}
                        />
                      </div>
                      <Button
                        secondary
                        label="Clear all filters"
                        onClick={resetFilters}
                        disabled={isAllControlsDefault}
                      />
                    </Flex>
                  }
                />
              )}
            </Box>
          </>
        )}
      </div>
    </Modal>
  );
};
